import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';

import { useCreateIngredient } from '../api/ingredients-mutations';

const newIngredientSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  title: z.string().min(1, 'Name is required'), // Use title instead of name to stop password-managers from flagging the field
});

type NewIngredientForm = z.infer<typeof newIngredientSchema>;

interface AddIngredientFormProps {
  selectedSupplierId: string | null;
  tenantId: string | null;
  onBack: () => void;
  onClose?: () => void;
  onFormReset?: () => void;
  title?: string;
}

export const AddIngredientForm = ({
  onBack,
  onClose,
  selectedSupplierId,
  tenantId,
  onFormReset,
  title = 'Add New Ingredient',
}: AddIngredientFormProps) => {
  const createIngredient = useCreateIngredient();
  const [isPending, setIsPending] = useState(false);
  const navigate = useNavigate({
    from: '/$tenantId/ingredients',
  });

  const form = useForm<NewIngredientForm>({
    resolver: zodResolver(newIngredientSchema),
    defaultValues: {
      code: '',
      title: '',
    },
  });

  const handleCreateIngredient = async (data: NewIngredientForm) => {
    if (!selectedSupplierId) return;

    setIsPending(true);

    const { data: ingredientID, error } = await createIngredient(
      tenantId ?? undefined,
      selectedSupplierId,
      data.title,
      data.code,
      'g', // TODO: Replace with actual display unit logic
      'ml', // TODO: Replace with actual display unit logic
    );

    setIsPending(false);

    if (error) {
      if (error.code === '23505') {
        form.setError('code', {
          type: 'manual',
          message: 'An ingredient with this code already exists.',
        });
      }
    } else {
      form.reset();
      onFormReset?.();
      onClose?.();
      await navigate({
        to: '/$tenantId/ingredients/$ingredientId',
        params: (prev) => ({
          ...prev,
          ingredientId: ingredientID,
        }),
      });
    }
  };

  return (
    <>
      <DialogHeader className="space-y-0">
        <DialogTitle className="text-md font-bold p-4 border-b border-gray-300">
          {title}
        </DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleCreateIngredient)}
          className="p-4 space-y-4"
        >
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Ingredient Name:</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ingredient Name"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Ingredient Code:</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ingredient Code"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex space-x-2 justify-between">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isValid || isPending}
              >
                {isPending ? 'Creating...' : 'Create Ingredient'}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};

// Keep the original interface for backward compatibility
interface AddOrganisationIngredientProps {
  selectedSupplierId: string | null;
  onBack: () => void;
  onClose: () => void;
}

export const AddOrganisationIngredient = ({
  onBack,
  onClose,
  selectedSupplierId,
}: AddOrganisationIngredientProps) => {
  // TODO: Match consistency of this elsewhere, namely split between presentation and container components
  const { tenantId } = useParams({
    from: '/_authenticated/$tenantId/ingredients/',
  });

  return (
    <AddIngredientForm
      selectedSupplierId={selectedSupplierId}
      tenantId={tenantId}
      onBack={onBack}
      onClose={onClose}
    />
  );
};
